import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { Observable } from 'rxjs';
import { ApplianceDetailsFacade } from '@modules/appliance-details/appliance-details.facade';
import { IdName } from '@shared/models/id-name';
import { debounceTime, distinctUntilChanged, take, takeUntil, tap } from 'rxjs/operators';
import { ApplianceDetailsState, createInitialState } from '@modules/appliance-details/state/appliance-details.store';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, AsyncPipe } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { IsDisabledDirective } from '@shared/directives/is-disabled/is-disabled.directive';
import { MatLegacySlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { APPLIANCE_TYPES } from '@modules/appliance-details/models/appliance-type.constants';
import { applianceOptionsArchClicked, applianceOptionTypeClicked } from '@core/services/logger/bi-actions';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import { observeJawSelection } from '@shared/utils/jaw.util';
import { debounceDelay } from '@shared/models/consts';
import { SelectComponent } from '@shared/components/select/select.component';

@Component({
	selector: 'rx-appliance-details',
	templateUrl: './appliance-details.component.html',
	styleUrls: ['./appliance-details.component.scss'],
	providers: [ApplianceDetailsFacade],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		FlexModule,
		ReactiveFormsModule,
		SelectComponent,
		MatLegacySlideToggleModule,
		MatLegacyFormFieldModule,
		IsDisabledDirective,
		MatDividerModule,
		NgIf,
		AsyncPipe,
		TranslateModule
	]
})
export class ApplianceDetailsComponent extends BaseDestroyableComponent implements OnInit {
	form = this.formBuilder.group<ApplianceDetailsState>(createInitialState());

	applianceType$: Observable<IdName> = this.facade.applianceType$;
	applianceTypes$: Observable<IdName[]> = this.facade.applianceTypes$;
	isReadonly$ = this.facade.isReadonly$;
	isApplianceTypeVisible$: Observable<boolean> = this.facade.isApplianceTypeVisible$;
	isApplianceUpperReadonly$: Observable<boolean> = this.facade.isApplianceUpperReadonly$;
	isApplianceLowerReadonly$: Observable<boolean> = this.facade.isApplianceLowerReadonly$;

	get isUpperControl() {
		return this.form.controls.isUpperChecked;
	}

	get isLowerControl() {
		return this.form.controls.isLowerChecked;
	}

	get applianceTypeControl() {
		return this.form.controls.applianceType;
	}

	constructor(
		private formBuilder: FormBuilder,
		private facade: ApplianceDetailsFacade,
		private biLogger: BiLoggerService
	) {
		super();
	}

	ngOnInit(): void {
		this.subscribeToUserChanges();
		this.subscribeToUserBiEvents();
		this.loadFromStore();
		this.subscribeToRxLoaded();
		this.subscribeToStoreChanges();
	}

	private loadFromStore(): void {
		this.form.patchValue(this.facade.storeState, { emitEvent: false });
	}

	private subscribeToUserChanges(): void {
		this.form.valueChanges
			.pipe(
				distinctUntilChanged(),
				tap(() => {
					// @ts-expect-error - TODO: remove this and fix the type error
					const result = this.facade.updateStore(this.form.getRawValue());

					this.form.patchValue(result.formValues, { emitEvent: false });
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToStoreChanges(): void {
		this.facade
			.subscribeToStoreChanges()
			.pipe(
				distinctUntilChanged(),
				tap(state => {
					this.form.patchValue(state, { emitEvent: false });
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToRxLoaded(): void {
		this.facade
			.getLoadedRx()
			.pipe(
				tap(state => this.form.patchValue(state, { emitEvent: false })),
				take(1),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToUserBiEvents(): void {
		this.applianceTypeControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.componentAlive$)).subscribe(({ Id }: IdName) => {
			const selectedType = APPLIANCE_TYPES.find(type => type.Id === Id);

			if (selectedType) {
				this.biLogger.userActionEvent(applianceOptionTypeClicked(selectedType.Name));
			}
		});

		observeJawSelection(this.isUpperControl as FormControl<boolean>, this.isLowerControl as FormControl<boolean>)
			.pipe(debounceTime(debounceDelay), distinctUntilChanged(), takeUntil(this.componentAlive$))
			.subscribe(selectedOption => this.biLogger.userActionEvent(applianceOptionsArchClicked(selectedOption)));
	}
}
